/**
 * FeedbackPrompt — reusable "PLEASE! help us improve" call to action.
 *
 * Two visual variants (both open the same modal):
 *   • variant="footer"  → tiny inline link in the site footer
 *   • variant="banner"  → prominent card, dropped at the end of exercises
 *
 * The modal auto-fills name/email from useAuth() if the leader / coach is
 * signed in. Submits to POST /api/feedback (see backend), which also emails
 * michaelmascolo@commongroundinstitute.net via Resend.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { ChatDots, MegaphoneSimple, PaperPlaneTilt, Spinner, X } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/errors";

export default function FeedbackPrompt({ variant = "footer", pageContext }) {
  const [open, setOpen] = useState(false);

  const trigger = variant === "banner" ? (
    <button
      onClick={() => setOpen(true)}
      data-testid="feedback-banner-trigger"
      className="w-full text-left bg-[#F3F0EA] border border-[#1E3A2B]/15 rounded-2xl p-5 sm:p-6 hover:border-[#1E3A2B]/40 transition group"
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-[#1E3A2B] text-white flex items-center justify-center flex-shrink-0">
          <MegaphoneSimple size={20} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-kicker" style={{ color: "#C77D5E" }}>Still under development</div>
          <p className="mt-2 text-[#1E3A2B] leading-relaxed">
            <strong>Please!</strong> We want your feedback. Help us
            improve.
          </p>
          <p className="mt-1 text-sm text-[#4A524C] leading-relaxed">
            Tell us what worked, what didn't, and what you wish were here — even
            one sentence helps.
          </p>
          <span className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#1E3A2B] font-medium group-hover:underline">
            Share feedback <PaperPlaneTilt size={14} weight="bold" />
          </span>
        </div>
      </div>
    </button>
  ) : (
    <button
      onClick={() => setOpen(true)}
      data-testid="feedback-footer-trigger"
      className="inline-flex items-center gap-1.5 text-xs text-[#4A524C] hover:text-[#1E3A2B] underline decoration-[#1E3A2B]/30 underline-offset-4"
    >
      <ChatDots size={12} weight="duotone" /> Still under development — help us improve
    </button>
  );

  return (
    <>
      {trigger}
      {open && <FeedbackModal onClose={() => setOpen(false)} pageContext={pageContext} />}
    </>
  );
}

function FeedbackModal({ onClose, pageContext }) {
  const { leader, coach } = useAuth();
  const identity = leader || coach || null;
  const [message, setMessage] = useState("");
  const [name, setName] = useState(identity?.name || "");
  const [email, setEmail] = useState(identity?.email || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 3) {
      toast.error("Please share a few words of feedback.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/feedback", {
        message: message.trim(),
        page: pageContext || (typeof window !== "undefined" ? window.location.pathname : null),
        name: name.trim() || null,
        email: email.trim() || null,
      });
      toast.success("Thank you — we read every note.");
      onClose();
    } catch (err) {
      toast.error(apiError(err, "Could not send feedback"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#121815]/60 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="feedback-modal"
    >
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl anim-in">
        <div className="flex items-start justify-between p-6 pb-4">
          <div>
            <div className="label-kicker" style={{ color: "#C77D5E" }}>Please!</div>
            <h2 className="font-serif text-2xl text-[#1E3A2B] mt-1 tracking-tight leading-tight">
              We want your feedback.
            </h2>
            <p className="text-sm text-[#4A524C] mt-2 leading-relaxed">
              This app is still under development. Tell us what worked, what
              didn't, or what you wish were here — even one sentence helps.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="feedback-modal-close"
            className="text-[#8A948E] hover:text-[#1E3A2B] p-1 -mr-1"
            aria-label="Close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
        <form onSubmit={submit} className="px-6 pb-6 space-y-3">
          <textarea
            data-testid="feedback-message-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            required
            autoFocus
            placeholder="What's on your mind?"
            className="w-full bg-white border border-[#E8E4DB] rounded-xl p-3 text-base text-[#121815] focus:outline-none focus:border-[#1E3A2B]"
          />
          {!identity && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                data-testid="feedback-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Name (optional)"
                className="bg-white border border-[#E8E4DB] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#1E3A2B]"
              />
              <input
                data-testid="feedback-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email (optional, only if you want a reply)"
                className="bg-white border border-[#E8E4DB] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#1E3A2B]"
              />
            </div>
          )}
          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-[#8A948E] hover:text-[#1E3A2B] px-3 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || message.trim().length < 3}
              data-testid="feedback-submit"
              className="inline-flex items-center gap-2 bg-[#1E3A2B] text-white px-5 py-2.5 rounded-xl hover:bg-[#2C4C3B] disabled:opacity-60"
            >
              {busy ? <Spinner size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="bold" />}
              Send it
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
